/**
 * The wire protocol, shared by the Durable Object, the dev relay, and the
 * client (U18).
 *
 * **Only commands cross the wire** (KTD4). No state is ever sent, which is why
 * bandwidth is flat in unit count: a few hundred bytes per second whether
 * twenty or four hundred units are moving.
 *
 * Control messages are JSON with the command set carried as base64. At three
 * submissions per tick of a handful of commands each, the encoding overhead is
 * irrelevant next to being able to read a capture in a terminal when a desync
 * has to be diagnosed at two in the morning.
 */

import { MAX_MESSAGE_BYTES } from '../core/config.js'
import { decodeSet, encodeSet, type Command } from './commands.js'

export const MSG = {
  // client → server
  Join: 'join',
  Submit: 'submit',
  Hash: 'hash',
  Leave: 'leave',
  /**
   * Keepalive. The lobby sends nothing at all while waiting for the second
   * player, and an idle WebSocket is a WebSocket something in the middle
   * eventually closes — which surfaced as a bare "Disconnected." after a couple
   * of minutes of waiting, with no fault anywhere in the game.
   */
  Ping: 'ping',
  /**
   * Chat.
   *
   * Deliberately **not** a simulation command. A command is ordered into the
   * tick stream, replayed on every client, and folded into the state hash;
   * routing a line of text through all of that would put arbitrary player
   * input inside the one part of the system whose whole value is that both
   * machines agree on it exactly. Chat has no effect on the world, so it
   * travels beside the tick stream and arrives when it arrives.
   */
  Chat: 'chat',
  // server → client
  Welcome: 'welcome',
  Commit: 'commit',
  Desync: 'desync',
  Drop: 'drop',
  Late: 'late',
  Error: 'error',
  Start: 'start',
} as const

export type MsgType = (typeof MSG)[keyof typeof MSG]

export interface JoinMsg {
  t: typeof MSG.Join
  code: string
  /** Presented on reconnect to reclaim a slot. */
  token?: string
  name?: string
  /**
   * The highest tick this client has a committed set for.
   *
   * Sent on a reconnect so the relay can find out where the match actually is.
   * It cannot work that out for itself after a restart: it persists its own
   * committed tick only every so often, so its copy lags, and the clients are
   * the only parties that know what they have really executed.
   */
  ack?: number
}

export interface SubmitMsg {
  t: typeof MSG.Submit
  tick: number
  /** base64 of `encodeSet`. Empty string for an empty set. */
  cmds: string
}

export interface HashMsg {
  t: typeof MSG.Hash
  tick: number
  hash: number
}

export interface WelcomeMsg {
  t: typeof MSG.Welcome
  slot: number
  token: string
  seed: string
  mapSeed: string
  slots: Array<{ faction: number; team: number; connected: boolean }>
  /** Non-zero when rejoining a match already running. */
  resumeTick: number
}

export interface StartMsg {
  t: typeof MSG.Start
  startTick: number
  slots: Array<{ faction: number; team: number }>
}

export interface CommitMsg {
  t: typeof MSG.Commit
  tick: number
  cmds: string
}

export interface DesyncMsg {
  t: typeof MSG.Desync
  from: number
  to: number
  hashes: Array<{ slot: number; hash: number }>
}

export interface DropMsg {
  t: typeof MSG.Drop
  slot: number
  /** The tick at which every remaining client applies the drop, identically. */
  atTick: number
  reason: string
}

export interface LateMsg {
  t: typeof MSG.Late
  slot: number
  tick: number
}

/**
 * A chat line, in both directions. `slot` is filled in by the relay — a client
 * saying which slot it is speaking for would be a client able to speak as
 * somebody else.
 */
export interface ChatMsg {
  t: typeof MSG.Chat
  text: string
  slot?: number
}

export interface ErrorMsg {
  t: typeof MSG.Error
  reason: string
  detail?: string
}

export type ClientMessage =
  | JoinMsg
  | SubmitMsg
  | HashMsg
  | ChatMsg
  | { t: typeof MSG.Leave }
  | { t: typeof MSG.Ping }
export type ServerMessage =
  | WelcomeMsg
  | StartMsg
  | CommitMsg
  | DesyncMsg
  | DropMsg
  | LateMsg
  | ChatMsg
  | ErrorMsg

/** The longest chat line the relay will carry. */
export const MAX_CHAT_LENGTH = 160

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

export function encodeCommands(commands: readonly Command[]): string {
  if (commands.length === 0) return ''
  return bytesToBase64(encodeSet(commands))
}

export function decodeCommands(text: string): Command[] | null {
  if (text === '') return []
  const bytes = base64ToBytes(text)
  return bytes ? decodeSet(bytes) : null
}

export function encodeMessage(msg: ClientMessage | ServerMessage): string {
  return JSON.stringify(msg)
}

/**
 * Parse a frame. Returns `null` for anything malformed or oversized — a hostile
 * peer must never be able to make the relay throw, and an oversized frame is
 * dropped **before** it is parsed rather than after (R35).
 */
export function decodeMessage(text: string): ClientMessage | ServerMessage | null {
  if (typeof text !== 'string') return null
  if (text.length > MAX_MESSAGE_BYTES) return null
  try {
    const parsed = JSON.parse(text) as { t?: unknown }
    if (!parsed || typeof parsed.t !== 'string') return null
    return parsed as ClientMessage | ServerMessage
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// base64, without assuming Node or the browser
// ---------------------------------------------------------------------------

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

export function bytesToBase64(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0
    out += B64[a >> 2]
    out += B64[((a & 3) << 4) | (b >> 4)]
    out += i + 1 < bytes.length ? B64[((b & 15) << 2) | (c >> 6)] : '='
    out += i + 2 < bytes.length ? B64[c & 63] : '='
  }
  return out
}

export function base64ToBytes(text: string): Uint8Array | null {
  const clean = text.replace(/=+$/, '')
  const n = clean.length
  const bytes = new Uint8Array(Math.floor((n * 3) / 4))
  let out = 0
  let acc = 0
  let bits = 0
  for (let i = 0; i < n; i++) {
    const v = B64.indexOf(clean[i])
    if (v < 0) return null
    acc = (acc << 6) | v
    bits += 6
    if (bits >= 8) {
      bits -= 8
      bytes[out++] = (acc >> bits) & 0xff
    }
  }
  return bytes.subarray(0, out)
}

/**
 * A match code with at least 40 bits of entropy, generated **server-side**.
 * Client-supplied codes are never accepted: a guessable code lets anyone walk
 * into a lobby, and a client-chosen one lets an attacker create a Durable
 * Object per distinct string.
 */
export function generateMatchCode(random: (n: number) => number): string {
  // 8 characters from a 32-symbol alphabet is 40 bits. Ambiguous glyphs are
  // omitted so a code can be read aloud.
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  for (let i = 0; i < 8; i++) code += alphabet[random(alphabet.length)]
  return code
}

export function isWellFormedCode(code: unknown): code is string {
  return typeof code === 'string' && /^[A-HJ-NP-Z2-9]{8}$/.test(code)
}
